import { center } from '@turf/turf'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import type { MaterializedFeatureCollection } from 'honeygrid-types'
import type { MapRef } from 'react-map-gl/mapbox'

const initialViewState = {
	latitude: 39.8283,
	longitude: -98.5795,
	zoom: 4.5,
	pitch: 15,
	bearing: 0,
}

export const useViewState = (
	isFetching: boolean,
	isSuccess: boolean,
	geoJson: MaterializedFeatureCollection | undefined,
	mapRef: React.RefObject<MapRef | null>
) => {
	const [viewState, setViewState] = useState(initialViewState)
	const [queryParams] = useSearchParams()
	const { gridId } = useParams<{ gridId?: string }>()
	const selectedGrid = gridId ?? queryParams.get('gridId')
	const windowLocation = useLocation()

	const centerMap = useCallback(() => {
		if (geoJson?.features.length && !isFetching && isSuccess) {
			try {
				const centerPoint = center(geoJson)
				mapRef.current?.easeTo({
					center: {
						lat: centerPoint.geometry?.coordinates[1],
						lon: centerPoint.geometry?.coordinates[0],
					},
					zoom: 9,
				})
			} catch (e) {
				console.log(e)
			}
		}
	}, [geoJson, isFetching, isSuccess, mapRef])

	// Logic for re-centering the map
	useEffect(() => {
		// If we have a selected grid or we're adding a new grid, center on that
		if (
			geoJson?.features.length &&
			(selectedGrid || windowLocation.pathname.includes('add')) &&
			!isFetching &&
			isSuccess
		) {
			centerMap()
		}
	}, [centerMap, geoJson, isFetching, isSuccess, mapRef, selectedGrid, windowLocation.pathname])

	return [viewState, setViewState, centerMap] as const
}
